import { DateCell } from "@/components/date/cell";
import { StatusCell } from "@/components/status/cell";

import { TypeDisplay } from "@/components/type/display";
import { EmployeeCell } from "components/cell/employee/cell";
import { useCurrencies } from "@/modules/reports/contexts/currency-context";
import { AmountCell } from "@/modules/work/billables/cells";

const getAllBillables = (row) => {
  const contacts = row.original.contacts || [];
  const billables = [];

  contacts.forEach((contact) => {
    const projects = contact.projects || [];
    projects.forEach((project) => {
      const jobs = project.jobs || [];
      jobs.forEach((job) => {
        const jobBillables = job.billables || [];
        jobBillables.forEach((billable) => {
          billables.push({
            ...billable,
            job,
            project,
          });
        });
      });
    });
  });

  return billables;
};

const getAllProjects = (row) => {
  const contacts = row.original.contacts || [];
  const projects = [];

  contacts.forEach((contact) => {
    const contactProjects = contact.projects || [];
    contactProjects.forEach((project) => {
      if (project && !projects.find((p) => p.id === project.id)) {
        projects.push(project);
      }
    });
  });

  return projects;
};

const getAllJobs = (row) => {
  const contacts = row.original.contacts || [];
  const jobs = [];

  contacts.forEach((contact) => {
    const projects = contact.projects || [];
    projects.forEach((project) => {
      const projectJobs = project.jobs || [];
      projectJobs.forEach((job) => {
        if (job && !jobs.find((j) => j.id === job.id)) {
          jobs.push(job);
        }
      });
    });
  });

  return jobs;
};

export const ProjectsCountCell = ({ row }) => {
  const projects = getAllProjects(row);
  return <span>{projects.length}</span>;
};

export const JobsCountCell = ({ row }) => {
  const jobs = getAllJobs(row);
  return <span>{jobs.length}</span>;
};

export const BillablesCountCell = ({ row }) => {
  const billables = getAllBillables(row);
  return <span>{billables.length}</span>;
};

export const JobTypesCell = ({ row }) => {
  const jobs = getAllJobs(row);
  const types = [...new Set(jobs.map((job) => job.type).filter(Boolean))];

  if (types.length === 0) return <span>-</span>;

  return (
    <div className="flex gap-1 flex-wrap">
      {types.map((type, index) => (
        <TypeDisplay value={type} key={index} />
      ))}
    </div>
  );
};

export const TotalAmountCell = ({ row }) => {
  const { convertToINR } = useCurrencies();
  const billables = getAllBillables(row);
  const totalAmount = billables.reduce((sum, billable) => {
    const amount = convertToINR(
      billable.adjusted_amount || billable.amount || 0,
    );
    return sum + amount;
  }, 0);

  if (!billables.length) return <span>-</span>;

  return (
    <AmountCell
      row={{
        original: { amount: totalAmount, currency: billables[0].currency.name },
      }}
    />
  );
};

export const EarliestBillingDateCell = ({ row }) => {
  const billables = getAllBillables(row);

  if (billables.length === 0) return <span>-</span>;

  const dates = billables
    .map((b) => b.billing_date)
    .filter(Boolean)
    .sort();

  const earliestDate = dates[0];

  return <DateCell getValue={() => earliestDate} />;
};

export const SalesCoordinatorsCell = ({ row }) => {
  const projects = getAllProjects(row);
  const coordinators = projects.map((p) => p.sales_coordinator).filter(Boolean);

  const uniqueCoordinators = coordinators.filter(
    (coord, index, self) =>
      coord && index === self.findIndex((c) => c?.id === coord?.id),
  );

  if (uniqueCoordinators.length === 0) return <span>-</span>;

  return (
    <div className="flex gap-1 flex-wrap">
      {uniqueCoordinators.map((coordinator, index) => (
        <EmployeeCell key={index} getValue={() => coordinator} />
      ))}
    </div>
  );
};

export const AccountManagersCell = ({ row }) => {
  const projects = getAllProjects(row);
  const managers = projects.map((p) => p.account_manager).filter(Boolean);

  const uniqueManagers = managers.filter(
    (manager, index, self) =>
      manager && index === self.findIndex((m) => m?.id === manager?.id),
  );

  if (uniqueManagers.length === 0) return <span>-</span>;

  return (
    <div className="flex gap-1 flex-wrap">
      {uniqueManagers.map((manager, index) => (
        <EmployeeCell key={index} getValue={() => manager} />
      ))}
    </div>
  );
};

export const ProjectCoordinatorsCell = ({ row }) => {
  const projects = getAllProjects(row);
  const coordinators = projects.map((p) => p.coordinator).filter(Boolean);

  const uniqueCoordinators = coordinators.filter(
    (coord, index, self) =>
      coord && index === self.findIndex((c) => c?.id === coord?.id),
  );

  if (uniqueCoordinators.length === 0) return <span>-</span>;

  return (
    <div className="flex gap-1 flex-wrap">
      {uniqueCoordinators.map((coordinator, index) => (
        <EmployeeCell key={index} getValue={() => coordinator} />
      ))}
    </div>
  );
};

export const JobCoordinatorsCountCell = ({ row }) => {
  const jobs = getAllJobs(row);
  const coordinators = jobs.map((j) => j.coordinator).filter(Boolean);

  // Count unique job coordinators
  const uniqueCount = coordinators.filter(
    (coord, index, self) =>
      coord && index === self.findIndex((c) => c?.id === coord?.id),
  ).length;

  return <span>{uniqueCount}</span>;
};

export const EarliestDueDateCell = ({ row }) => {
  const billables = getAllBillables(row);

  if (billables.length === 0) return <span>-</span>;

  const dates = billables
    .map((b) => b.due_date)
    .filter(Boolean)
    .sort();

  const earliestDate = dates[0];

  return <DateCell getValue={() => earliestDate} />;
};

export const EarliestCollectedDateCell = ({ row }) => {
  const billables = getAllBillables(row);

  if (billables.length === 0) return <span>-</span>;

  const dates = billables
    .map((b) => b.collected_date)
    .filter(Boolean)
    .sort();

  const earliestDate = dates[0];

  return <DateCell getValue={() => earliestDate} />;
};

export const EarliestForecastDateCell = ({ row }) => {
  const billables = getAllBillables(row);
  if (billables.length === 0) return <span>-</span>;

  const dates = billables
    .map((b) => b.forecast_date)
    .filter(Boolean)
    .sort();

  const earliestDate = dates[0];

  return <DateCell getValue={() => earliestDate} />;
};

const baseParentColumns = {
  name: {
    id: "name",
    header: "Company",
    accessorKey: "name",
  },
  projects_count: {
    id: "projects_count",
    header: "Projects",
    cell: ProjectsCountCell,
  },
  jobs_count: {
    id: "jobs_count",
    header: "Jobs",
    cell: JobsCountCell,
  },
  billables_count: {
    id: "billables_count",
    header: "Billables",
    cell: BillablesCountCell,
  },
  job_types: {
    id: "job_types",
    header: "Type",
    cell: JobTypesCell,
  },
  total_amount: {
    id: "total_amount",
    header: "Amount",
    cell: TotalAmountCell,
  },
  earliest_billing_date: {
    id: "contacts.projects.jobs.billables.billing_date",
    header: "Billing Date",
    cell: EarliestBillingDateCell,
    sortable: true,
  },
  earliest_due_date: {
    id: "earliest_due_date",
    header: "Due Date",
    cell: EarliestDueDateCell,
    sortable: true,
  },
  earliest_collected_date: {
    id: "earliest_collected_date",
    header: "Collected Date",
    cell: EarliestCollectedDateCell,
    sortable: true,
  },
  earliest_forecaste_date: {
    id: "contacts.projects.jobs.billables.forecast_date",
    header: "Forecast Date",
    cell: EarliestForecastDateCell,
    sortable: true,
  },
  project_coordinators: {
    id: "project_coordinators",
    header: "PC",
    cell: ProjectCoordinatorsCell,
  },
  account_managers: {
    id: "account_managers",
    header: "AM",
    cell: AccountManagersCell,
  },
  sales_coordinators: {
    id: "sales_coordinators",
    header: "SC",
    cell: SalesCoordinatorsCell,
  },
  status: {
    id: "status",
    header: "Status",
    accessorKey: "status",
    cell: StatusCell,
  },
};

const parentColumnModules = {
  contracted: [
    "name",
    "projects_count",
    "jobs_count",
    "billables_count",
    "job_types",
    "total_amount",
    "earliest_billing_date",
    "project_coordinators",
    "account_managers",
    "sales_coordinators",
  ],
  pending_invoice: [
    "name",
    "projects_count",
    "jobs_count",
    "billables_count",
    "job_types",
    "total_amount",
    "earliest_billing_date",
    "project_coordinators",
    "account_managers",
    "sales_coordinators",
  ],
  billed: [
    "name",
    "projects_count",
    "jobs_count",
    "billables_count",
    "job_types",
    "total_amount",
    "earliest_forecaste_date",
    "project_coordinators",
    "account_managers",
    "sales_coordinators",
  ],
  collected: [
    "name",
    "projects_count",
    "jobs_count",
    "billables_count",
    "job_types",
    "total_amount",
    "earliest_collected_date",
    "project_coordinators",
    "account_managers",
    "sales_coordinators",
  ],
  cancelled: [
    "name",
    "projects_count",
    "jobs_count",
    "billables_count",
    "job_types",
    "total_amount",
    "earliest_billing_date",
    "project_coordinators",
    "account_managers",
    "sales_coordinators",
  ],
  all: [
    "name",
    "projects_count",
    "jobs_count",
    "billables_count",
    "job_types",
    "total_amount",
    "earliest_billing_date",
    "project_coordinators",
    "account_managers",
    "sales_coordinators",
  ],
};

export const getCompanyBasedColumns = (status) => {
  const keys = parentColumnModules[status] || parentColumnModules.contracted;
  return keys.map((key) => baseParentColumns[key]);
};
